/**
 * @file carousel.tsx
 * Main Material Design 3 Expressive Carousel component.
 *
 * Provides Dual-Render API (RSC children vs CSR items + renderItem)
 * and Hybrid "Show All" Control Pattern (Uncontrolled internal state vs Controlled router delegation).
 *
 * @see docs/m3/carousel/Carousel_Specs_Material_Design_3.md
 * @see docs/m3/carousel/Carousel_Accessibility_Material_Design_3.md
 */

import * as React from "react";
import { cn } from "../../lib/utils";
import type { CarouselProps } from "./carousel.types";
import { CarouselGrid } from "./carousel-grid";
import { CarouselScrollClient } from "./carousel-scroll";

export function Carousel<T = unknown>({
	children,
	items,
	renderItem,
	layout = "multi-browse",
	preferredItemWidth = 300,
	itemSpacing = 8,
	contentPadding = 16,
	verticalPadding = 8,
	isExpanded: controlledExpanded,
	onShowAllClick,
	showAllLabel = "Show all",
	userScrollEnabled = true,
	className,
	style,
	...props
}: CarouselProps<T>) {
	// Uncontrolled internal state for "Show all" grid toggle
	const [internalExpanded, setInternalExpanded] =
		React.useState<boolean>(false);

	const isExpanded = controlledExpanded ?? internalExpanded;

	// Calculate total items count
	const totalItemsCount = React.useMemo(() => {
		if (items) return items.length;
		return React.Children.count(children);
	}, [items, children]);

	const handleShowAllToggle = React.useCallback(() => {
		if (onShowAllClick) {
			onShowAllClick();
		} else {
			setInternalExpanded((prev) => !prev);
		}
	}, [onShowAllClick]);

	const isFullScreen = layout === "full-screen";

	return (
		<div
			className={cn(
				"relative w-full flex flex-col gap-3 box-border min-h-0",
				// Full-screen: propagate height to track
				isFullScreen && "flex-1",
				className,
			)}
			style={style}
		>
			{/* Show All Header Control (hidden in full-screen variant) */}
			{!isFullScreen && (
				<div
					className="flex items-center justify-between"
					style={{ padding: `0 ${contentPadding}px` }}
				>
					{props["aria-label"] && (
						<h3 className="m-0 text-(length:--md-sys-typescale-title-medium-size,1.125rem) font-medium text-(--md-sys-color-on-surface,currentColor)">
							{props["aria-label"]}
						</h3>
					)}
					<button
						type="button"
						className={cn(
							"inline-flex items-center justify-center min-h-12 min-w-12 px-4 py-2",
							"rounded-[20px] border border-(--md-sys-color-outline-variant,rgba(121,116,126,0.3))",
							"bg-transparent text-(--md-sys-color-primary,#6750a4) font-medium text-[0.875rem]",
							"cursor-pointer outline-none transition-colors duration-200",
							"hover:bg-(--md-sys-color-surface-container-high,rgba(103,80,164,0.08))",
							"focus-visible:outline-[3px] focus-visible:outline-(--md-sys-color-secondary,#625b71) focus-visible:outline-offset-2",
						)}
						onClick={handleShowAllToggle}
						aria-expanded={isExpanded}
						aria-label={
							isExpanded
								? "Collapse carousel view"
								: `${showAllLabel} (${totalItemsCount} items)`
						}
					>
						{isExpanded ? "Show carousel" : showAllLabel}
					</button>
				</div>
			)}

			{/* Main View: Responsive Grid or Scroll Carousel */}
			{isExpanded ? (
				<CarouselGrid
					items={items}
					renderItem={renderItem}
					preferredItemWidth={preferredItemWidth}
				>
					{children}
				</CarouselGrid>
			) : (
				<CarouselScrollClient
					items={items}
					renderItem={renderItem}
					layout={layout}
					preferredItemWidth={preferredItemWidth}
					itemSpacing={itemSpacing}
					contentPadding={contentPadding}
					verticalPadding={verticalPadding}
					userScrollEnabled={userScrollEnabled}
					totalItemsCount={totalItemsCount}
					{...props}
				>
					{children}
				</CarouselScrollClient>
			)}
		</div>
	);
}
